import { useCallback, useEffect } from 'react';

import usePhonetic from '@replacements/hooks/usePhonetic';
import { useReplacementsDictionaryContext } from '@replacements/context/ReplacementsDictionaryContext';
import usePrevious from 'lib/usePrevious';
import { useEventsContext } from 'context/events/EventsContext';
import {
  EventName,
  ReplacementsDictionaryLocation,
} from 'services/EventTrackingService';

/**
 * Modifies the given replacement to be replaced with a respelling the user
 * selects from the dictionary, if any
 */
const useDictionaryReplacement = ({
  replacement,
  setIsRespelling,
}: {
  replacement: ReturnType<typeof usePhonetic>['replacement'];
  setIsRespelling: (value: boolean) => void;
}) => {
  /**
   * Listen to replacement from dictionary flyout and update
   * the replacement field & is respelling toggle
   */
  const {
    showDictionary,
    replacement: dictionaryReplacement,
    setWordInput,
    setShowDictionary,
    reset,
  } = useReplacementsDictionaryContext();

  const [eventTracking] = useEventsContext();

  const prevReplacement = usePrevious(dictionaryReplacement);
  useEffect(() => {
    if (prevReplacement !== dictionaryReplacement && dictionaryReplacement) {
      replacement.onTextChange(dictionaryReplacement);
      setIsRespelling(true);
    }
  }, [dictionaryReplacement, prevReplacement, replacement, setIsRespelling]);

  const handleDictionaryIconClick = useCallback(
    (word: string, location: ReplacementsDictionaryLocation) => {
      setShowDictionary(!showDictionary);
      setWordInput(word);
      eventTracking.track({
        type: EventName.ReplacementsDictionaryShown,
        data: {
          location,
          input: word,
        },
      });
    },
    [eventTracking, setShowDictionary, setWordInput, showDictionary]
  );

  return {
    reset,
    handleDictionaryIconClick,
  };
};

export default useDictionaryReplacement;
